import { createContext, useContext, useEffect, useState } from "react";

import { Message } from "../screens/Message";
import { resolveApiBase } from "../api/base";
import { useT } from "../i18n/LocaleProvider";

// ---------------------------------------------------------------------------
// V55 (S0-279's second slice, D102-1). The owner's banner and screen switches.
//
// A SCREEN SWITCH HIDES THE SCREEN; IT IS NOT AUTHORISATION. Other screens read the same routes, so the API
// keeps answering; the sign-up, checkout and email switches are the ones the Worker enforces. If this read
// fails, nothing is hidden and no banner shows: the console can never take the product down by failing.
// ---------------------------------------------------------------------------

export type Controls = { banner: { text: string; tone: "info" | "warn" | "err" } | null; modules: Record<string, boolean> };
const OPEN: Controls = { banner: null, modules: {} };
const ControlsContext = createContext<Controls>(OPEN);

export function ControlsProvider({ children }: { children: React.ReactNode }) {
  const [controls, setControls] = useState<Controls>(OPEN);
  useEffect(() => {
    const base = resolveApiBase(import.meta.env.VITE_API_BASE, window.location.origin);
    void fetch(`${base}/v1/controls`)
      .then((res) => (res.ok ? (res.json() as Promise<Controls>) : OPEN))
      .then(setControls, () => undefined);
  }, []);
  const { banner } = controls;
  return (
    <ControlsContext.Provider value={controls}>
      {banner ? (
        <p className={`notice ${banner.tone === "err" ? "notice-failed" : banner.tone === "warn" ? "notice-attention" : "notice-neutral"}`} role="status">
          {banner.text}
        </p>
      ) : null}
      {children}
    </ControlsContext.Provider>
  );
}

export function ScreenSwitch({ id, children }: { id: string; children: React.ReactNode }) {
  const { modules } = useContext(ControlsContext);
  const { t } = useT();
  if (modules[id] !== false) return <>{children}</>;
  return <Message tone="attention" status={t("Paused")} title={t("This screen is paused for now.")} />;
}
